import React from 'react';
import { RotateCcw } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle
} from '@epoch-ui/components/Modal/modal';

import {
  ScheduleImpactCard,
  ScheduleImpactCardProps
} from '../ScheduleImpactCard/ScheduleImpactCard';

export enum TimeChangeConfirmModalType {
  CONFIRM = 'confirm',
  DISPLAY = 'display'
}

interface TimeChangeConfirmModalProps extends React.ComponentProps<typeof Modal> {
  timeChangeValidations: (ScheduleImpactCardProps | null)[];
  type: TimeChangeConfirmModalType;
  isSession?: boolean;
  onReschedule: () => void;
  onConfirm?: () => void;
}

export const TimeChangeConfirmModal = ({
  timeChangeValidations,
  type,
  isSession = false,
  onReschedule,
  onConfirm,
  ...props
}: TimeChangeConfirmModalProps): React.ReactElement => {
  const isConfirmModal = type === TimeChangeConfirmModalType.CONFIRM;
  const eventType = isSession ? 'session' : 'event';

  return (
    <Modal {...props}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>
            {isConfirmModal ? 'Confirm event time change?' : 'Scheduled features'}
          </ModalTitle>
        </ModalHeader>

        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-m-0 tw-text-sm tw-leading-5 tw-text-black">
            There is a conflict with the timing of scheduled features. If you change the {eventType}{' '}
            time, this will change when communications are sent and when features are effective:
          </p>

          <div className="tw-flex tw-flex-col tw-gap-2">
            {timeChangeValidations.map(
              (validation, index) =>
                validation && <ScheduleImpactCard key={index} {...validation} />
            )}
          </div>

          <p className="tw-m-0 tw-text-sm tw-leading-5 tw-text-black">
            To prevent this, reset the {eventType} time and reschedule when the notification should
            be sent before you update the {eventType} time.
          </p>
        </div>

        <ModalFooter className="tw-mt-5">
          {isConfirmModal ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                className="tw-rounded-xl"
                onClick={onReschedule}
              >
                Reschedule
              </Button>
              <Button variant="primary" size="sm" className="tw-rounded-xl" onClick={onConfirm}>
                Confirm
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              className="tw-gap-1 tw-rounded-xl"
              onClick={onReschedule}
            >
              <RotateCcw className="tw-h-4 tw-w-4" />
              Reset time
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
